'use client';

import React from 'react';
import { styled } from '@linaria/react';
import { LayoutGroup, motion } from 'framer-motion';

import { BREAKPOINTS } from '@/constants';

interface FullOption {
  label: string;
  value: string;
  shortLabel?: string;
}
type Option = string | FullOption;

export interface Props extends React.HTMLAttributes<HTMLDivElement> {
  title?: string;
  options: Array<Option>;
  value: string | Record<string, boolean>;
  handleSelect: React.Dispatch<React.SetStateAction<string>>;
}

function SleekToggleButtonGroup({
  title,
  options,
  value,
  handleSelect,
  ...delegated
}: Props) {
  const isMultiSelect = getIsMultiSelect(value);
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  const buttonRefs = React.useRef<Array<HTMLButtonElement>>([]);
  const formattedOptions = options.map((opt) => {
    return typeof opt === 'object'
      ? opt
      : {
          label: opt,
          value: opt,
        };
  });

  return (
    <LayoutGroup>
      <Wrapper {...delegated}>
        {title && <Title>{title}</Title>}
        <Row layoutRoot>
          {formattedOptions.map((option, index) => {
            const isSelected = isMultiSelect
              ? value[option.value] === true
              : value === option.value;

            return (
              <ToggleButton
                key={option.value}
                ref={(elem) => {
                  if (elem) {
                    buttonRefs.current[index] = elem;
                  }
                }}
                value={option.value}
                tabIndex={index === selectedIndex ? undefined : -1}
                onClick={() => {
                  handleSelect(option.value);
                  setSelectedIndex(index);
                }}
                onKeyDown={(event) => {
                  let nextIndex;

                  // Filter out any options that are hidden with CSS
                  const validOptions = formattedOptions.filter(
                    (_, index) => {
                      const button = buttonRefs.current[index];

                      if (!button) {
                        return false;
                      }
                      const style = window.getComputedStyle(button);
                      return style?.display !== 'none';
                    }
                  );

                  if (event.code === 'ArrowLeft') {
                    nextIndex = selectedIndex - 1;

                    // Wrap around:
                    if (nextIndex < 0) {
                      nextIndex = validOptions.length - 1;
                    }
                  } else if (event.code === 'ArrowRight') {
                    nextIndex = selectedIndex + 1;

                    if (nextIndex >= validOptions.length) {
                      nextIndex = 0;
                    }
                  }

                  if (typeof nextIndex === 'number') {
                    // Move focus
                    buttonRefs.current[nextIndex]?.focus();

                    // In single-select mode, change the selected option
                    if (!isMultiSelect) {
                      handleSelect(formattedOptions[nextIndex].value);
                    }

                    setSelectedIndex(nextIndex);
                  }
                }}
                style={{
                  opacity: isSelected ? 1 : 0.65,
                }}
              >
                <Background
                  layout
                  style={{
                    opacity: isSelected ? 1 : 0.4,
                    height: isSelected ? 4 : 2,
                    filter: isSelected
                      ? 'saturate(100%)'
                      : 'saturate(0%)',
                  }}
                />
                {!!option.shortLabel ? (
                  <>
                    <ShortText>{option.shortLabel}</ShortText>
                    <LongText>{option.label}</LongText>
                  </>
                ) : (
                  <Text>{option.label}</Text>
                )}
              </ToggleButton>
            );
          })}
        </Row>
      </Wrapper>
    </LayoutGroup>
  );
}

function getIsMultiSelect(
  value: any
): value is Record<string, boolean> {
  return typeof value === 'object' && value !== null;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const Title = styled.div`
  font-size: 1rem;
  font-weight: var(--font-weight-medium);
`;

const Row = styled(motion.div)`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const ToggleButton = styled.button`
  position: relative;
  padding: 8px 16px;
  color: var(--color-text);
  font-size: 1rem;
  white-space: nowrap;

  &:focus-visible {
    outline-color: hsl(50deg 100% 50%);
    outline-offset: 2px;
  }
`;

const Background = styled(motion.span)`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  background: hsl(50deg 100% 50%);
  border-radius: 16px;
`;

const Text = styled.span`
  position: relative;
`;

const ShortText = styled(Text)`
  display: none;
  @media ${BREAKPOINTS.smAndSmaller} {
    display: revert;
  }
`;
const LongText = styled(Text)`
  @media ${BREAKPOINTS.smAndSmaller} {
    display: none;
  }
`;

export default SleekToggleButtonGroup;
